import {
  LayoutDashboard,
  Building2,
  Truck,
  UserCog,
  Ticket,
  Images,
  Factory,
  Package,
  ShoppingCart,
  ClipboardList,
  Users,
  Handshake,
  DollarSign,
  Wrench,
  Briefcase,
  Plug,
  LineChart,
  Shield,
  Settings,
  FileSpreadsheet,
  FlaskConical,
  Bell,
  Contact,
  ListChecks,
  Wallet,
  LayoutGrid,
  type LucideIcon,
} from "lucide-react";
// ═ COMPANY-STRIP BEGIN ═
import { Megaphone } from "lucide-react";
// ═ COMPANY-STRIP END ═
import { hasAnyPermission } from "@/auth/permissions";
import { NAV_PERMS } from "@/auth/routePermissions";

export type NavSection = "main" | "resources" | "business" | "ops" | "system";

export interface NavItem {
  id: string;
  to: string;
  icon: LucideIcon;
  labelCn: string;
  labelEn: string;
  section: NavSection;
  badge?: number;
  /** PAY-1 · 侧栏子项（紧跟父项、缩进显示、不生成占位路由）。to 可带 ?stage= 查询串。 */
  sub?: boolean;
  // ═ COMPANY-STRIP BEGIN ═ COMPANY-CUT-1
  /** SAAS-2 · 只在订阅版（个人站）出现的菜单项；企业版（公司站）直接过滤掉，菜单一项不多 */
  subscriptionOnly?: boolean;
  // ═ COMPANY-STRIP END ═
}

/**
 * Sidebar 18 NavItem · 严格按 T-FE-01 工单顺序
 * 角色过滤参考 ota_v1_delivery_roadmap.md + 工单约束
 */
export const NAV_ITEMS: NavItem[] = [
  // main
  { id: "home", to: "/", icon: LayoutDashboard, labelCn: "首页", labelEn: "Home", section: "main" },

  // resources（资源类）
  { id: "hotels", to: "/hotels", icon: Building2, labelCn: "酒店管理", labelEn: "Hotels", section: "resources" },
  { id: "vehicles", to: "/vehicles", icon: Truck, labelCn: "车辆管理", labelEn: "Vehicles", section: "resources" },
  { id: "drivers", to: "/drivers", icon: UserCog, labelCn: "司导管理", labelEn: "Drivers", section: "resources" },
  { id: "attractions", to: "/attractions", icon: Ticket, labelCn: "门票管理", labelEn: "Attractions", section: "resources" },
  // IMGLIB-1 · 图片库：全公司产品图片集中管理（紧跟门票管理）
  { id: "media-library", to: "/media-library", icon: Images, labelCn: "图片库", labelEn: "Media Library", section: "resources" },
  { id: "suppliers", to: "/suppliers", icon: Factory, labelCn: "供应商管理", labelEn: "Suppliers", section: "resources" },
  { id: "products", to: "/products", icon: Package, labelCn: "产品管理", labelEn: "Products", section: "resources" },

  // business（商务类）
  { id: "orders", to: "/orders", icon: ShoppingCart, labelCn: "订单中心", labelEn: "Orders", section: "business" },
  { id: "site-payments-orders", to: "/admin/site-payments?stage=screen", icon: Wallet, labelCn: "官网收款", labelEn: "Site Payments", section: "business", sub: true },
  { id: "customers", to: "/customers", icon: Contact, labelCn: "CRM 中心", labelEn: "CRM", section: "business" },
  { id: "handoff", to: "/ops/handoff", icon: ClipboardList, labelCn: "OP 交接中心", labelEn: "OP Handoff", section: "business" },
  { id: "dispatch", to: "/dispatch", icon: Users, labelCn: "调度中心", labelEn: "Dispatch", section: "business" },
  { id: "channel-partners", to: "/admin/channel-partners", icon: Handshake, labelCn: "同业 B2B", labelEn: "Channel Partners", section: "business" },
  // OPENAPI-2 · 开放平台审批（接入申请 + 上线申请），带待办角标
  { id: "open-api-approvals", to: "/admin/channel-partners/open-api", icon: Plug, labelCn: "开放平台审批", labelEn: "Open API Approvals", section: "business", sub: true },
  { id: "pricing", to: "/admin/pricing", icon: DollarSign, labelCn: "售价管理中心", labelEn: "Pricing", section: "business" },
  // LISTCTR-1 · 上架中心（从渠道中心拆出）：紧跟售价管理中心
  { id: "listing", to: "/admin/listing", icon: ListChecks, labelCn: "上架中心", labelEn: "Listing Center", section: "business" },
  // ═ COMPANY-STRIP BEGIN ═
  // MKT-1 · 内容营销（个人站订阅版一级模块）：只在 flags.marketing（订阅版 + 已开通）时显示，见 visibleNavItems
  { id: "marketing", to: "/marketing", icon: Megaphone, labelCn: "内容营销", labelEn: "Content Marketing", section: "business", subscriptionOnly: true },
  // ═ COMPANY-STRIP END ═

  // ops（运营类）
  { id: "vehicle-operations", to: "/admin/vehicle-operations", icon: Wrench, labelCn: "车务台账", labelEn: "Vehicle Operations", section: "ops" },
  {
    id: "finance",
    to: "/admin/finance",
    icon: Briefcase,
    labelCn: "财务",
    labelEn: "Finance",
    section: "ops",
  },
  { id: "site-payments-finance", to: "/admin/site-payments?stage=audit", icon: Wallet, labelCn: "官网收款", labelEn: "Site Payments", section: "ops", sub: true },
  {
    id: "api-integration",
    to: "/admin/api-integration",
    icon: Plug,
    labelCn: "渠道中心",
    labelEn: "Channel Center",
    section: "ops",
  },
  { id: "analytics", to: "/ops/analytics", icon: LineChart, labelCn: "运营总览", labelEn: "Analytics", section: "ops" },

  // system（系统类）
  {
    id: "permissions",
    to: "/admin/permissions",
    icon: Shield,
    labelCn: "权限管理",
    labelEn: "Permissions",
    section: "system",
  },
  {
    id: "settings",
    to: "/admin/settings",
    icon: Settings,
    labelCn: "系统设置",
    labelEn: "Settings",
    section: "system",
  },
  {
    id: "import-tool",
    to: "/admin/system/import",
    icon: FileSpreadsheet,
    labelCn: "数据导入",
    labelEn: "Data Import",
    section: "system",
  },
  {
    id: "demo-tools",
    to: "/admin/system/demo-tools",
    icon: FlaskConical,
    labelCn: "演示工具",
    labelEn: "Demo Tools",
    section: "system",
  },
  // T-S2-B-4 · 系统告警中心
  {
    id: "system-alerts",
    to: "/admin/system/alerts",
    icon: Bell,
    labelCn: "系统告警",
    labelEn: "System Alerts",
    section: "system",
  },
  // ═ COMPANY-STRIP BEGIN ═ COMPANY-CUT-1
  // SAAS-2 · 应用中心（仅订阅版；顶部导航在「更多」里，同样稿 ③）
  { id: "app-center", to: "/apps", icon: LayoutGrid, labelCn: "应用中心", labelEn: "App Center", section: "system", subscriptionOnly: true },
  // ═ COMPANY-STRIP END ═
];

export const SECTION_LABELS: Record<NavSection, string> = {
  main: "主要",
  resources: "资源类",
  business: "商务类",
  ops: "运营类",
  system: "系统",
};

/**
 * RBAC-ENF1 · 侧栏按「权限点」显示（不再按角色名）：每个菜单项需要的权限点见 auth/routePermissions.ts 的 NAV_PERMS，
 * 用户有其中任意一个才显示；没出现在 NAV_PERMS 里的（首页）人人可见。
 * permissions 为 undefined（旧会话 / mock）时先不过滤，/me 回来后立即收紧。
 */
/** 版本 / 模块开关类的显示条件（按租户设置，不是按权限）：没传 = 全部按「关」处理，菜单与改动前一致。 */
export type NavFlags = Readonly<Record<string, boolean>>;

/** 菜单项 id → 需要打开的开关名（模块开通类）。表里的项只有 flags[开关名] 为 true 才显示——与版本、权限过滤叠加。 */
export const NAV_FLAG_GATES: Readonly<Record<string, string>> = {
  // ═ COMPANY-STRIP BEGIN ═
  marketing: "marketing",   // MKT-1 · 内容营销已启用（已开通 / 试用中）
  // ═ COMPANY-STRIP END ═
};

export function visibleNavItems(
  permissions: readonly string[] | undefined,
  // ═ COMPANY-STRIP BEGIN ═ COMPANY-CUT-1
  edition: "enterprise" | "subscription" = "enterprise",
  // ═ COMPANY-STRIP END ═
  flags: NavFlags = {},
): NavItem[] {
  return NAV_ITEMS.filter((it) => {
    // ═ COMPANY-STRIP BEGIN ═
    if (it.subscriptionOnly && edition !== "subscription") return false;   // SAAS-2 · 订阅版专属菜单：企业版不出
    // ═ COMPANY-STRIP END ═
    const gate = NAV_FLAG_GATES[it.id];
    if (gate && !flags[gate]) return false;
    const need = NAV_PERMS[it.id];
    return !need || hasAnyPermission(permissions, need);
  }).map((it) => {
    // IMGLIB-1 · 运营经理没有「系统设置」权限，但能管「地点清单」：菜单仍显示「系统设置」，直接进地点清单那个标签（其它标签他本来就没有）
    if (it.id === "settings" && permissions && !hasAnyPermission(permissions, ["system.settings.view"])) return { ...it, to: "/admin/settings/places" };
    return it;
  });
}

// ═ COMPANY-STRIP BEGIN ═ COMPANY-CUT-1 · 顶部模块导航（只给 THEME-1 后台版式 topnav 用）
// ───────────────────────────── THEME-1 · 顶部模块导航（admin_layout = topnav）─────────────────────────────

/**
 * 一级模块：从**同一份** NAV_ITEMS 分组生成（不另写一份菜单）。每个菜单项必须且只能属于一个模块（测试盯着，防加了菜单项却在顶部导航里丢了）。
 * 权限过滤照旧走 visibleNavItems（RBAC-ENF1）：没有任何可见项的模块整个不显示。
 * 最后一个模块 `more` 在顶栏渲染成「更多 ▾」下拉。
 */
export interface NavModule {
  id: string;
  labelCn: string;
  itemIds: string[];
}

export const NAV_MODULES: NavModule[] = [
  { id: "today", labelCn: "今日", itemIds: ["home"] },
  { id: "orders", labelCn: "订单", itemIds: ["orders", "site-payments-orders", "customers", "handoff"] },
  { id: "dispatch", labelCn: "团期与调度", itemIds: ["dispatch", "vehicle-operations"] },
  { id: "products", labelCn: "产品", itemIds: ["products", "pricing", "media-library"] },
  { id: "resources", labelCn: "资源", itemIds: ["hotels", "vehicles", "drivers", "attractions", "suppliers"] },
  { id: "channels", labelCn: "渠道与上架", itemIds: ["listing", "api-integration"] },
  { id: "partners", labelCn: "同行", itemIds: ["channel-partners", "open-api-approvals"] },
  { id: "marketing", labelCn: "内容营销", itemIds: ["marketing"] },   // MKT-1（本段整段已在公司版切除区内）
  { id: "finance", labelCn: "财务", itemIds: ["finance", "site-payments-finance"] },
  { id: "more", labelCn: "更多", itemIds: ["analytics", "permissions", "settings", "import-tool", "demo-tools", "system-alerts", "app-center"] },
];

export interface VisibleModule extends NavModule {
  items: NavItem[];
}

/** 可见菜单项（已按权限过滤）→ 有可见项的模块（保持 NAV_MODULES 的顺序，模块内保持 NAV_ITEMS 的顺序）。 */
export function groupVisibleModules(items: NavItem[]): VisibleModule[] {
  return NAV_MODULES.map((m) => ({ ...m, items: items.filter((it) => m.itemIds.includes(it.id)) })).filter((m) => m.items.length > 0);
}

const stageGroup = (s: string | null) => (s === "audit" || s === "exception" || s === "done" ? "finance" : "orders");

/** 这个菜单项是不是当前页（与侧栏 SidebarLink 同一套判定）。 */
export function isNavItemActive(item: NavItem, pathname: string, search: string): boolean {
  const [toPath, toQuery] = item.to.split("?");
  if (item.sub) {
    return pathname === toPath && stageGroup(new URLSearchParams(search).get("stage")) === stageGroup(new URLSearchParams(toQuery ?? "").get("stage"));
  }
  if (item.to === "/") return pathname === "/";
  return pathname === item.to || pathname.startsWith(item.to + "/");
}

/** 当前页属于哪个模块（取 `to` 最长的那个匹配项，避免 /admin/finance 与 /admin/site-payments 之类前缀串位）。找不到 = null。 */
export function activeModuleId(modules: VisibleModule[], pathname: string, search: string): string | null {
  let best: { id: string; len: number } | null = null;
  for (const m of modules) {
    for (const it of m.items) {
      if (isNavItemActive(it, pathname, search)) {
        const len = it.to.split("?")[0].length;
        if (!best || len > best.len) best = { id: m.id, len };
      }
    }
  }
  return best?.id ?? null;
}
// ═ COMPANY-STRIP END ═
